import {pulseUserAtom} from 'atoms/auth';
import {getGrowthbookOverrideUserKey} from 'atoms/growthbook';
import {useAtom, useAtomValue} from 'jotai';
import {atomWithStorage} from 'jotai/utils';
import {useCallback, useMemo} from 'react';

/**
 * Which MCP clients (ChatGPT, Cursor, …) a user has marked connected.
 *
 * Connecting an AI app to the CompanyOS MCP happens entirely in the external
 * app, so there is no server signal to read back. We instead record the
 * self-reported connection locally (per user, so a shared browser doesn't leak
 * one person's connections to another) and use it to skip straight to the
 * "connected" state on return visits. The set of connected clients is
 * user-global — it is not scoped to a single workstream.
 */
type ConnectionsByUser = Record<string, string[]>;

const connectionsAtom = atomWithStorage<ConnectionsByUser>('workstream-mcp-connections', {});

export const useWorkstreamMcpConnections = () => {
  const user = useAtomValue(pulseUserAtom);
  const userKey = getGrowthbookOverrideUserKey(user);
  const [all, setAll] = useAtom(connectionsAtom);

  const connectedClientIds = useMemo(() => all[userKey] ?? [], [all, userKey]);

  const connectClient = useCallback(
    (clientId: string) => {
      setAll((prev) => {
        const existing = prev[userKey] ?? [];
        const without = existing.filter((id) => id !== clientId);
        return {...prev, [userKey]: [clientId, ...without]};
      });
    },
    [setAll, userKey]
  );

  const disconnectClient = useCallback(
    (clientId: string) => {
      setAll((prev) => {
        const existing = prev[userKey] ?? [];
        return {...prev, [userKey]: existing.filter((id) => id !== clientId)};
      });
    },
    [setAll, userKey]
  );

  return {connectedClientIds, connectClient, disconnectClient};
};
